import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../core/api.service';
import { ALL_MENU_PERMISSION_KEYS, MENU_PERMISSIONS, MenuPermissionKey } from '../../core/menu-permissions';
import { PercentualPagamento, Usuario } from '../../core/models';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf],
  template: `
    <header class="page-header">
      <div><h2>Usuários</h2><p>Acessos internos do sistema</p></div>
      <button type="button" (click)="novoUsuario()">Novo usuário</button>
    </header>

    <div class="tabs">
      <button type="button" class="tab-button" [class.active]="aba === 'consulta'" (click)="aba = 'consulta'">Consulta</button>
      <button type="button" class="tab-button" [class.active]="aba === 'cadastro'" (click)="aba = 'cadastro'">Cadastro e edição</button>
    </div>

    <section class="panel" *ngIf="aba === 'cadastro'">
      <form class="grid-form" [formGroup]="form" (ngSubmit)="salvar()">
        <input type="hidden" formControlName="id_usuario">
        <label>Nome<input formControlName="nome"></label>
        <label>Email<input type="email" formControlName="email"></label>
        <label>CPF<input formControlName="cpf" inputmode="numeric" placeholder="Informe para cadastrar ou alterar"></label>
        <label>Chave Pix<input formControlName="chave_pix"></label>
        <label>Percentual de pagamento padrão (%)<input formControlName="percentual_pagamento" inputmode="decimal"></label>
        <div class="full payment-percentage-schedule" *ngIf="form.controls.id_usuario.value">
          <span class="field-label">Programar novo percentual</span>
          <div class="grid-form">
            <label>Novo percentual (%)<input formControlName="percentual_pagamento_programado" inputmode="decimal" placeholder="Ex.: 75"></label>
            <label>Válido a partir de<input type="date" formControlName="data_inicio_percentual"></label>
          </div>
          <p class="field-hint">A data é inclusiva. Retiradas anteriores continuam usando a regra anterior.</p>
          <table class="data-table" *ngIf="percentuaisProgramados.length">
            <thead><tr><th>Percentual</th><th>Válido a partir de</th></tr></thead>
            <tbody>
              <tr *ngFor="let percentual of percentuaisProgramados">
                <td data-label="Percentual">{{ percentual.percentual_pagamento }}%</td>
                <td data-label="Válido a partir de">{{ formatDate(percentual.data_inicio) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="field-hint full" *ngIf="!form.controls.id_usuario.value">
          Salve o usuário primeiro para programar alterações futuras no percentual.
        </p>
        <label>Tipo de usuário
          <select formControlName="tipo_usuario" (change)="tipoUsuarioAlterado()">
            <option value="ADMINISTRADOR">Administrador</option>
            <option value="EXPEDICAO">Expedição</option>
          </select>
        </label>
        <label>Senha<input type="password" formControlName="senha" placeholder="Preencha para alterar"></label>
        <label class="check"><input type="checkbox" formControlName="ativo"> Ativo</label>
        <div class="full menu-permissions" *ngIf="form.controls.tipo_usuario.value === 'ADMINISTRADOR'">
          <span class="field-label">Menus liberados</span>
          <div class="permission-grid">
            <label class="check permission-option" *ngFor="let opcao of opcoesMenu">
              <input
                type="checkbox"
                [checked]="temPermissao(opcao.key)"
                (change)="alternarPermissao(opcao.key, $event)"
              >
              {{ opcao.label }}
            </label>
          </div>
        </div>
        <p class="field-hint full" *ngIf="form.controls.tipo_usuario.value === 'EXPEDICAO'">
          Expedição terá acesso somente ao dashboard e às próprias contagens.
        </p>
        <button type="submit" [disabled]="form.invalid">Salvar usuário</button>
        <button class="ghost" type="button" (click)="limpar()">Limpar</button>
        <button class="ghost" type="button" (click)="aba = 'consulta'">Voltar</button>
      </form>
      <p class="error" *ngIf="erro">{{ erro }}</p>
    </section>

    <table class="data-table" *ngIf="aba === 'consulta'">
      <thead><tr><th>Nome</th><th>Email</th><th>Tipo</th><th>Chave Pix</th><th>Percentual</th><th>Status</th><th></th></tr></thead>
      <tbody>
        <tr *ngFor="let usuario of usuarios">
          <td data-label="Nome">{{ usuario.nome }}</td>
          <td data-label="Email">{{ usuario.email || usuario.login }}</td>
          <td data-label="Tipo">{{ usuario.tipo_usuario === 'EXPEDICAO' ? 'Expedição' : 'Administrador' }}</td>
          <td data-label="Chave Pix">{{ usuario.chave_pix || '-' }}</td>
          <td data-label="Percentual">{{ usuario.percentual_pagamento === undefined ? 100 : usuario.percentual_pagamento }}%</td>
          <td data-label="Status"><span class="status" [class.cancelada]="!usuario.ativo">{{ usuario.ativo ? 'Ativo' : 'Inativo' }}</span></td>
          <td class="actions" data-label="Ações">
            <button type="button" (click)="editar(usuario)">Editar</button>
            <button type="button" (click)="inativar(usuario)" [disabled]="!usuario.ativo">Inativar</button>
          </td>
        </tr>
      </tbody>
    </table>
  `
})
export class UsuariosComponent implements OnInit {
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  usuarios: Usuario[] = [];
  erro = '';
  opcoesMenu = MENU_PERMISSIONS;
  aba: 'consulta' | 'cadastro' = 'consulta';
  percentuaisProgramados: PercentualPagamento[] = [];

  form = this.fb.nonNullable.group({
    id_usuario: [0],
    nome: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    cpf: [''],
    chave_pix: [''],
    percentual_pagamento: [100, [Validators.required, Validators.min(0), Validators.max(100)]],
    percentual_pagamento_programado: [''],
    data_inicio_percentual: [''],
    tipo_usuario: ['ADMINISTRADOR' as Usuario['tipo_usuario'], Validators.required],
    senha: [''],
    ativo: [true],
    permissoes_menu: [ALL_MENU_PERMISSION_KEYS]
  });

  ngOnInit() {
    this.carregar();
  }

  carregar() {
    this.api.usuarios().subscribe((usuarios) => (this.usuarios = usuarios));
  }

  salvar() {
    this.erro = '';
    const raw = this.form.getRawValue();
    if (!raw.id_usuario && !raw.senha) {
      this.erro = 'Senha obrigatória para novo usuário';
      return;
    }
    if (raw.permissoes_menu.length === 0) {
      this.erro = 'Informe ao menos uma opção do menu';
      return;
    }
    const payload = {
      ...raw,
      id_usuario: raw.id_usuario || undefined,
      email: raw.email.trim().toLowerCase(),
      cpf: raw.cpf.trim() || undefined,
      chave_pix: raw.chave_pix.trim() || undefined,
      percentual_pagamento: this.parsePercentual(raw.percentual_pagamento),
      percentual_pagamento_programado: this.parsePercentualOpcional(raw.percentual_pagamento_programado),
      data_inicio_percentual: raw.data_inicio_percentual || undefined,
      tipo_usuario: raw.tipo_usuario,
      senha: raw.senha || undefined
    };
    this.api.salvarUsuario(payload).subscribe({
      next: () => {
        this.limpar();
        this.carregar();
        this.aba = 'consulta';
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao salvar usuário')
    });
  }

  editar(usuario: Usuario) {
    this.form.patchValue({
      id_usuario: usuario.id_usuario,
      nome: usuario.nome,
      email: usuario.email || usuario.login || '',
      cpf: '',
      chave_pix: usuario.chave_pix || '',
      percentual_pagamento: usuario.percentual_pagamento ?? 100,
      percentual_pagamento_programado: '',
      data_inicio_percentual: '',
      tipo_usuario: usuario.tipo_usuario || 'ADMINISTRADOR',
      senha: '',
      ativo: usuario.ativo,
      permissoes_menu: usuario.permissoes_menu?.length ? usuario.permissoes_menu : ALL_MENU_PERMISSION_KEYS
    });
    this.percentuaisProgramados = usuario.percentuais_pagamento || [];
    this.aba = 'cadastro';
  }

  inativar(usuario: Usuario) {
    this.api.inativarUsuario(usuario.id_usuario).subscribe(() => this.carregar());
  }

  limpar() {
    this.form.reset({
      id_usuario: 0,
      nome: '',
      email: '',
      cpf: '',
      chave_pix: '',
      percentual_pagamento: 100,
      percentual_pagamento_programado: '',
      data_inicio_percentual: '',
      tipo_usuario: 'ADMINISTRADOR',
      senha: '',
      ativo: true,
      permissoes_menu: ALL_MENU_PERMISSION_KEYS
    });
    this.percentuaisProgramados = [];
  }

  novoUsuario() {
    this.limpar();
    this.aba = 'cadastro';
  }

  tipoUsuarioAlterado() {
    if (this.form.controls.tipo_usuario.value === 'EXPEDICAO') {
      this.form.controls.permissoes_menu.setValue(['dashboard', 'contagens_nova', 'contagens']);
    } else if (!this.form.controls.permissoes_menu.value.length
      || this.form.controls.permissoes_menu.value.length === 2
      || this.form.controls.permissoes_menu.value.length === 3) {
      this.form.controls.permissoes_menu.setValue(ALL_MENU_PERMISSION_KEYS);
    }
  }

  temPermissao(permissao: MenuPermissionKey) {
    return this.form.controls.permissoes_menu.value.includes(permissao);
  }

  alternarPermissao(permissao: MenuPermissionKey, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const permissoes = this.form.controls.permissoes_menu.value;
    const novasPermissoes = checked
      ? Array.from(new Set([...permissoes, permissao]))
      : permissoes.filter((item) => item !== permissao);

    this.form.controls.permissoes_menu.setValue(novasPermissoes);
  }

  private parsePercentual(value: string | number) {
    const parsed = Number(String(value ?? '100').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 100;
  }

  private parsePercentualOpcional(value: string | number) {
    if (value === undefined || value === null || String(value).trim() === '') return undefined;
    return this.parsePercentual(value);
  }

  formatDate(value: string) {
    const [ano, mes, dia] = String(value || '').slice(0, 10).split('-');
    return ano && mes && dia ? `${dia}/${mes}/${ano}` : value;
  }
}
